import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { toast } from "sonner";
import {
  Package,
  Plus,
  Send,
  MessageCircle,
  Eye,
  Check,
  X,
  Clock,
  AlertTriangle,
  ChevronRight,
  Trash2,
  Car,
  Phone,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Copy,
  Search,
  ChevronsUpDown,
  Loader2,
} from "lucide-react";
import { autoPartsDescriptions } from "../data/autoParts";
import { authFetch } from "../services/authService";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  draft: "bg-zinc-500/20 text-zinc-400",
  sent: "bg-blue-500/20 text-blue-400",
  viewed: "bg-yellow-500/20 text-yellow-400",
  responded: "bg-green-500/20 text-green-400",
  accepted: "bg-emerald-500/20 text-emerald-400",
  rejected: "bg-red-500/20 text-red-400",
  in_lavorazione: "bg-cyan-500/20 text-cyan-400",
  quotato: "bg-purple-500/20 text-purple-400",
  confermato: "bg-green-500/20 text-green-400",
  rifiutato: "bg-red-500/20 text-red-400",
};

const statusLabels = {
  draft: "Bozza",
  sent: "Inviata",
  viewed: "Visualizzata",
  responded: "Risposta Ricevuta",
  accepted: "Accettata",
  rejected: "Rifiutata",
  in_lavorazione: "In Lavorazione",
  quotato: "Preventivo Ricevuto",
  confermato: "Confermato",
  rifiutato: "Rifiutato",
};

const urgencyLabels = {
  standard: "Standard",
  morning: "Urgente - In Mattinata",
  evening: "Urgente - In Serata",
  today: "Urgente - Oggi",
  vehicle_down: "Fermo Macchina",
};

const urgencyColors = {
  standard: "bg-zinc-500/20 text-zinc-400",
  morning: "bg-yellow-500/20 text-yellow-400",
  evening: "bg-amber-500/20 text-amber-400",
  today: "bg-orange-500/20 text-orange-400",
  vehicle_down: "bg-red-500/20 text-red-400",
};

export default function PartsRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [plateSearchLoading, setPlateSearchLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    quote_id: "",
    vehicle: {
      vin: "",
      brand: "",
      model: "",
      version: "",
      year: new Date().getFullYear(),
      engine_code: "",
      plate: "",
      km: 0,
    },
    parts: [{ description: "", code: "", quantity: 1, notes: "" }],
    urgency: "standard",
    supplier_name: "",
    supplier_phone: "",
    notes: "",
  });

  const fetchRequests = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/parts-requests`);
      if (res.ok) {
        const data = await res.json();
        setRequests(data);
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchQuotes = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/quotes`);
      if (res.ok) {
        const data = await res.json();
        setQuotes(data);
      }
    } catch (error) {
      console.error("Error fetching quotes:", error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/suppliers`);
      if (res.ok) {
        const data = await res.json();
        setSuppliers(data);
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchQuotes();
    fetchSuppliers();
  }, [fetchRequests]);

  const handleSupplierSelect = (supplierId) => {
    if (supplierId === "manual") {
      setFormData((prev) => ({
        ...prev,
        supplier_name: "",
        supplier_phone: "",
      }));
      return;
    }
    const supplier = suppliers.find((s) => s.supplier_id === supplierId);
    if (supplier) {
      setFormData((prev) => ({
        ...prev,
        supplier_name: supplier.name || "",
        supplier_phone: supplier.phone || "",
      }));
    }
  };

  const handleQuoteSelect = (quoteId) => {
    const quote = quotes.find((q) => q.quote_id === quoteId);
    if (quote) {
      // Filter out labor items (is_labor: true) - only keep parts/articles
      const partsOnly = (quote.items || []).filter(item => !item.is_labor);
      setFormData((prev) => ({
        ...prev,
        quote_id: quoteId,
        vehicle: {
          vin: quote.vehicle_vin || "",
          brand: quote.vehicle_brand || "",
          model: quote.vehicle_model || "",
          version: quote.vehicle_version || "",
          year: quote.vehicle_year || new Date().getFullYear(),
          engine_code: quote.vehicle_engine_code || "",
          plate: quote.vehicle_plate || "",
          km: quote.vehicle_km || 0,
        },
        // Code field left empty for manual entry by user
        parts: partsOnly.length > 0 ? partsOnly.map((item) => ({
          description: item.description || "",
          code: "", // Leave code empty for user to fill
          quantity: item.quantity || 1,
          notes: "",
        })) : [{ description: "", code: "", quantity: 1, notes: "" }],
      }));
    }
  };

  const addPart = () => {
    setFormData((prev) => ({
      ...prev,
      parts: [...prev.parts, { description: "", code: "", quantity: 1, notes: "" }],
    }));
  };

  const removePart = (index) => {
    setFormData((prev) => ({
      ...prev,
      parts: prev.parts.filter((_, i) => i !== index),
    }));
  };

  const updatePart = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      parts: prev.parts.map((part, i) =>
        i === index ? { ...part, [field]: value } : part
      ),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.supplier_name || !formData.supplier_phone) {
      toast.error("Inserisci nome e telefono del fornitore");
      return;
    }
    if (!formData.vehicle.brand || !formData.vehicle.model) {
      toast.error("Inserisci almeno marca e modello del veicolo");
      return;
    }
    if (!formData.vehicle.plate) {
      toast.error("Inserisci la targa del veicolo");
      return;
    }
    // Codice motore ora è facoltativo
    if (formData.parts.length === 0 || !formData.parts[0].description) {
      toast.error("Inserisci almeno un ricambio");
      return;
    }

    try {
      const res = await authFetch(`${API_URL}/api/parts-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        toast.success("Richiesta ricambi creata");
        setShowNewDialog(false);
        resetForm();
        fetchRequests();
      } else {
        toast.error("Errore nella creazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const resetForm = () => {
    setFormData({
      quote_id: "",
      vehicle: {
        vin: "",
        brand: "",
        model: "",
        version: "",
        year: new Date().getFullYear(),
        engine_code: "",
        plate: "",
        km: 0,
      },
      parts: [{ description: "", code: "", quantity: 1, notes: "" }],
      urgency: "standard",
      supplier_name: "",
      supplier_phone: "",
      notes: "",
    });
  };

  // Handle response to ricambista quote
  const handleRespondToQuote = async (requestId, action, markup = 0) => {
    try {
      const url = markup > 0 
        ? `${API_URL}/api/parts-requests/${requestId}/respond-to-quote?markup=${markup}`
        : `${API_URL}/api/parts-requests/${requestId}/respond-to-quote`;
      
      const res = await authFetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowDetailDialog(false);
        setShowRicambistaAcceptDialog(false);
        setAcceptingRicambistaQuote(null);
        setRicambistaMarkup(0);
        fetchRequests();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };
  
  // Open ricambista quote acceptance dialog with markup
  const openRicambistaAcceptDialog = (request) => {
    setAcceptingRicambistaQuote(request);
    setRicambistaMarkup(0);
    setShowRicambistaAcceptDialog(true);
  };

  const sendRequest = async (request) => {
    try {
      const res = await authFetch(
        `${API_URL}/api/parts-requests/${request.request_id}/send`,
        {
          method: "PATCH",
        }
      );

      if (res.ok) {
        const data = await res.json();
        
        // Build the public link
        const publicLink = `${window.location.origin}/risposta-ricambi/${data.token}`;
        const whatsappText = data.whatsapp_text + publicLink;
        
        // Clean phone number for WhatsApp
        const cleanPhone = request.supplier_phone.replace(/\D/g, "");
        const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(whatsappText)}`;
        
        // Open WhatsApp
        window.open(whatsappUrl, "_blank");
        
        toast.success("Richiesta inviata");
        fetchRequests();
      } else {
        toast.error("Errore nell'invio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  // State for accept confirmation dialog
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [acceptingRequest, setAcceptingRequest] = useState(null);
  const [markupPercentage, setMarkupPercentage] = useState(0);
  
  // State for ricambista quote acceptance dialog
  const [showRicambistaAcceptDialog, setShowRicambistaAcceptDialog] = useState(false);
  const [acceptingRicambistaQuote, setAcceptingRicambistaQuote] = useState(null);
  const [ricambistaMarkup, setRicambistaMarkup] = useState(0);

  const openAcceptDialog = (request) => {
    setAcceptingRequest(request);
    setMarkupPercentage(0);
    setShowAcceptDialog(true);
  };

  const acceptResponse = async () => {
    if (!acceptingRequest) return;
    
    try {
      const res = await authFetch(
        `${API_URL}/api/parts-requests/${acceptingRequest.request_id}/accept?markup=${markupPercentage}`,
        {
          method: "PATCH",
        }
      );

      if (res.ok) {
        const data = await res.json();
        let msg = "Risposta accettata";
        if (data.quote_updated) {
          msg += ". Preventivo aggiornato con i ricambi";
          if (markupPercentage > 0) {
            msg += ` (+${markupPercentage}% markup)`;
          }
        }
        if (data.order_created) {
          msg += ". Ordine fornitore creato";
        }
        toast.success(msg);
        fetchRequests();
        setShowDetailDialog(false);
        setShowAcceptDialog(false);
        setAcceptingRequest(null);
      } else {
        toast.error("Errore nell'accettazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const deleteRequest = async (requestId, skipConfirm = false) => {
    if (!skipConfirm && !window.confirm("Eliminare questa richiesta?")) return;

    try {
      const res = await authFetch(`${API_URL}/api/parts-requests/${requestId}`, {
        method: "DELETE",
      });

      if (res.ok) {
        toast.success("Richiesta eliminata");
        fetchRequests();
        setShowDetailDialog(false);
      } else {
        toast.error("Errore nell'eliminazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/risposta-ricambi/${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copiato negli appunti");
  };

  // Filter by status and search term
  const filteredRequests = requests.filter(r => {
    // Status filter
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    
    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const vehicle = r.vehicle || {};
      const matchesPlate = vehicle.plate?.toLowerCase().includes(term);
      const matchesBrand = vehicle.brand?.toLowerCase().includes(term);
      const matchesModel = vehicle.model?.toLowerCase().includes(term);
      const matchesVin = vehicle.vin?.toLowerCase().includes(term);
      const matchesSupplier = r.supplier_name?.toLowerCase().includes(term);
      const matchesNumber = r.request_number?.toLowerCase().includes(term);
      const matchesParts = r.parts?.some(p => 
        p.description?.toLowerCase().includes(term) || 
        p.code?.toLowerCase().includes(term)
      );
      
      return matchesPlate || matchesBrand || matchesModel || matchesVin || 
             matchesSupplier || matchesNumber || matchesParts;
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parts-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight">RICHIESTE RICAMBI</h1>
          <p className="text-zinc-500 mt-1">
            Invia richieste strutturate ai tuoi fornitori
          </p>
        </div>
        <Button
          onClick={() => setShowNewDialog(true)}
          className="gap-2"
          data-testid="new-request-btn"
        >
          <Plus className="w-4 h-4" />
          NUOVA RICHIESTA
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="stat-card">
          <Clock className="w-6 h-6 text-blue-500" />
          <div>
            <div className="stat-value">
              {requests.filter((r) => r.status === "sent" || r.status === "viewed").length}
            </div>
            <div className="stat-label">In attesa</div>
          </div>
        </div>
        <div className="stat-card">
          <CheckCircle2 className="w-6 h-6 text-green-500" />
          <div>
            <div className="stat-value">
              {requests.filter((r) => r.status === "responded").length}
            </div>
            <div className="stat-label">Con risposta</div>
          </div>
        </div>
        <div className="stat-card">
          <Check className="w-6 h-6 text-emerald-500" />
          <div>
            <div className="stat-value">
              {requests.filter((r) => r.status === "accepted").length}
            </div>
            <div className="stat-label">Accettate</div>
          </div>
        </div>
        <div className="stat-card">
          <AlertTriangle className="w-6 h-6 text-orange-500" />
          <div>
            <div className="stat-value">
              {requests.filter((r) => r.urgency === "vehicle_down").length}
            </div>
            <div className="stat-label">Fermo macchina</div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            type="text"
            placeholder="Cerca per targa, marca, modello, fornitore..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800"
            data-testid="search-parts-requests"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filtra per stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="draft">Bozze</SelectItem>
            <SelectItem value="sent">Inviate</SelectItem>
            <SelectItem value="viewed">Visualizzate</SelectItem>
            <SelectItem value="responded">Con risposta</SelectItem>
            <SelectItem value="accepted">Accettate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Requests Grid */}
      {filteredRequests.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>{searchTerm ? "Nessun risultato trovato" : "Nessuna richiesta trovata"}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800 text-left text-xs text-zinc-500 uppercase">
                <th className="py-3 px-4 font-medium">N°</th>
                <th className="py-3 px-4 font-medium">Stato</th>
                <th className="py-3 px-4 font-medium">Urgenza</th>
                <th className="py-3 px-4 font-medium">Veicolo</th>
                <th className="py-3 px-4 font-medium">Targa</th>
                <th className="py-3 px-4 font-medium">Fornitore</th>
                <th className="py-3 px-4 font-medium text-center">Ricambi</th>
                <th className="py-3 px-4 font-medium text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((request) => (
                <tr
                  key={request.request_id}
                  className="border-b border-zinc-800/50 hover:bg-zinc-900/50 cursor-pointer transition-colors"
                  onClick={() => {
                    setSelectedRequest(request);
                    setShowDetailDialog(true);
                  }}
                  data-testid={`request-card-${request.request_id}`}
                >
                  <td className="py-3 px-4">
                    <span className="text-primary font-bold">#{request.request_number}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded text-xs uppercase ${statusColors[request.status]}`}>
                      {statusLabels[request.status]}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded text-xs uppercase ${urgencyColors[request.urgency]}`}>
                      {request.urgency === "vehicle_down" && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                      {urgencyLabels[request.urgency]}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-white">{request.vehicle?.brand} {request.vehicle?.model}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-mono text-zinc-300">{request.vehicle?.plate || "N/D"}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-zinc-400">{request.supplier_name}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="text-zinc-400">{request.parts?.length || 0}</span>
                  </td>
                  <td className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-zinc-400 hover:text-red-500"
                      onClick={() => setDeleteConfirm(request)}
                      title="Elimina richiesta"
                      data-testid={`delete-request-${request.request_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New Request Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova Richiesta Ricambi</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Link to Quote */}
            <div>
              <Label className="text-zinc-300">Collega a Preventivo (opzionale)</Label>
              <Select
                value={formData.quote_id || "none"}
                onValueChange={(val) => handleQuoteSelect(val === "none" ? "" : val)}
              >
                <SelectTrigger className="bg-white text-zinc-900 border-zinc-300">
                  <SelectValue placeholder="Seleziona un preventivo..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun collegamento</SelectItem>
                  {quotes.map((quote) => (
                    <SelectItem key={quote.quote_id} value={quote.quote_id}>
                      #{quote.quote_number} - {quote.customer_name} ({quote.vehicle_plate})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Supplier Info */}
            <div className="space-y-4 p-4 bg-zinc-800/50 rounded-lg">
              <div>
                <Label className="text-zinc-300">Seleziona Fornitore dall'Archivio</Label>
                <Select
                  onValueChange={handleSupplierSelect}
                >
                  <SelectTrigger className="bg-white text-zinc-900 border-zinc-300">
                    <SelectValue placeholder="Seleziona un fornitore..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Inserisci manualmente</SelectItem>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.supplier_id} value={supplier.supplier_id}>
                        {supplier.name} {supplier.phone && `- ${supplier.phone}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Nome Fornitore *</Label>
                  <Input
                    value={formData.supplier_name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, supplier_name: e.target.value }))
                    }
                    placeholder="Es: Ricambi Auto SRL"
                    className="bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
                    data-testid="supplier-name-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Telefono WhatsApp *</Label>
                  <Input
                    value={formData.supplier_phone}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, supplier_phone: e.target.value }))
                    }
                    placeholder="Es: +39 333 1234567"
                    className="bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
                    data-testid="supplier-phone-input"
                  />
                </div>
              </div>
            </div>

            {/* Vehicle Info */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-white">
                <Car className="w-5 h-5" />
                <span className="font-semibold">Dati Veicolo</span>
              </div>
              
              {/* Search by plate */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-zinc-300">Targa *</Label>
                  <Input
                    value={formData.vehicle.plate}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        vehicle: { ...prev.vehicle, plate: e.target.value.toUpperCase() },
                      }))
                    }
                    placeholder="AA123BB"
                    className="uppercase bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
                    data-testid="vehicle-plate-input"
                  />
                </div>
                <Button
                  type="button"
                  disabled={plateSearchLoading}
                  onClick={async () => {
                    const plate = formData.vehicle.plate?.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                    if (!plate || plate.length < 5) {
                      toast.error("Inserisci una targa valida (minimo 5 caratteri)");
                      return;
                    }
                    setPlateSearchLoading(true);
                    try {
                      const res = await authFetch(`${API_URL}/api/vehicles/lookup/${plate}`);
                      if (res.ok) {
                        const data = await res.json();
                        setFormData(prev => ({
                          ...prev,
                          vehicle: {
                            ...prev.vehicle,
                            plate: data.licensePlate || plate,
                            brand: data.brand || prev.vehicle.brand,
                            model: data.model || prev.vehicle.model,
                            version: data.modelDetail || data.version || prev.vehicle.version,
                            year: data.buildYear || data.year || prev.vehicle.year,
                            vin: data.vin || prev.vehicle.vin,
                            engine_code: data.engine_code || prev.vehicle.engine_code,
                          }
                        }));
                        const sourceText = data.source === 'cache' ? 'archivio locale' : 'TuttoTarghe';
                        toast.success(`Dati veicolo caricati (${sourceText})`);
                      } else if (res.status === 404) {
                        toast.info("Veicolo non trovato - inserisci i dati manualmente");
                      } else {
                        toast.error("Errore nella ricerca");
                      }
                    } catch (error) {
                      toast.error("Errore di connessione");
                    } finally {
                      setPlateSearchLoading(false);
                    }
                  }}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="search-plate-btn"
                >
                  {plateSearchLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4 mr-2" />
                  )}
                  Cerca
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-zinc-300">Marca *</Label>
                  <Input
                    value={formData.vehicle.brand}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        vehicle: { ...prev.vehicle, brand: e.target.value },
                      }))
                    }
                    placeholder="Es: BMW"
                    className="bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Modello *</Label>
                  <Input
                    value={formData.vehicle.model}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        vehicle: { ...prev.vehicle, model: e.target.value },
                      }))
                    }
                    placeholder="Es: Serie 3"
                    className="bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Versione</Label>
                  <Input
                    value={formData.vehicle.version}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        vehicle: { ...prev.vehicle, version: e.target.value },
                      }))
                    }
                    placeholder="Es: 320d"
                    className="bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Anno</Label>
                  <Input
                    type="number"
                    value={formData.vehicle.year}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        vehicle: { ...prev.vehicle, year: parseInt(e.target.value) || 0 },
                      }))
                    }
                    className="bg-white text-zinc-900 border-zinc-300"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Codice Motore</Label>
                  <Input
                    value={formData.vehicle.engine_code}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        vehicle: { ...prev.vehicle, engine_code: e.target.value },
                      }))
                    }
                    placeholder="Es: N47D20A"
                    className="bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-zinc-300">VIN / Telaio</Label>
                  <Input
                    value={formData.vehicle.vin}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        vehicle: { ...prev.vehicle, vin: e.target.value.toUpperCase() },
                      }))
                    }
                    placeholder="Es: WBAPH5C55BA123456"
                    className="bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Km</Label>
                  <Input
                    type="number"
                    value={formData.vehicle.km}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        vehicle: { ...prev.vehicle, km: parseInt(e.target.value) || 0 },
                      }))
                    }
                    className="bg-white text-zinc-900 border-zinc-300"
                  />
                </div>
              </div>
            </div>

            {/* Parts */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <Package className="w-5 h-5" />
                  <span className="font-semibold">Ricambi Richiesti</span>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addPart}>
                  <Plus className="w-4 h-4 mr-1" />
                  Aggiungi
                </Button>
              </div>

              {formData.parts.map((part, index) => (
                <div
                  key={index}
                  className="grid grid-cols-12 gap-2 p-3 bg-zinc-800/30 rounded-lg items-end"
                >
                  <div className="col-span-5">
                    <Label className="text-xs text-zinc-300">Descrizione *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between bg-white text-zinc-900 border-zinc-300 hover:bg-zinc-100 font-normal"
                        >
                          <span className={part.description ? "text-zinc-900" : "text-zinc-400"}>
                            {part.description || "Seleziona o digita..."}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-zinc-500" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[350px] p-0" align="start">
                        <Command>
                          <CommandInput 
                            placeholder="Cerca ricambio..." 
                            value={part.description}
                            onValueChange={(value) => updatePart(index, "description", value)}
                            className="bg-white text-zinc-900"
                          />
                          <CommandList>
                            <CommandEmpty>
                              <span className="text-zinc-500">Nessun risultato. Usa il testo digitato.</span>
                            </CommandEmpty>
                            <CommandGroup className="max-h-[200px] overflow-y-auto">
                              {autoPartsDescriptions
                                .filter(desc => desc.toLowerCase().includes((part.description || "").toLowerCase()))
                                .slice(0, 50)
                                .map((desc) => (
                                  <CommandItem
                                    key={desc}
                                    value={desc}
                                    onSelect={() => {
                                      updatePart(index, "description", desc);
                                    }}
                                    className="cursor-pointer hover:bg-zinc-100"
                                  >
                                    <Check
                                      className={`mr-2 h-4 w-4 ${
                                        part.description === desc ? "opacity-100" : "opacity-0"
                                      }`}
                                    />
                                    {desc}
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs text-zinc-300">Codice</Label>
                    <Input
                      value={part.code}
                      onChange={(e) => updatePart(index, "code", e.target.value)}
                      placeholder="Es: 11427566327"
                      className="bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-zinc-300">Qtà</Label>
                    <Input
                      type="number"
                      min="1"
                      value={part.quantity}
                      onChange={(e) =>
                        updatePart(index, "quantity", parseInt(e.target.value) || 1)
                      }
                      className="bg-white text-zinc-900 border-zinc-300"
                    />
                  </div>
                  <div className="col-span-2 flex justify-end">
                    {formData.parts.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePart(index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Urgency */}
            <div>
              <Label className="text-zinc-300">Urgenza</Label>
              <Select
                value={formData.urgency}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, urgency: value }))
                }
              >
                <SelectTrigger className="bg-white text-zinc-900 border-zinc-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="morning">Urgente - In Mattinata</SelectItem>
                  <SelectItem value="evening">Urgente - In Serata</SelectItem>
                  <SelectItem value="today">Urgente - Consegna oggi</SelectItem>
                  <SelectItem value="vehicle_down">FERMO MACCHINA</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-zinc-300">Note aggiuntive</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Eventuali note per il fornitore..."
                rows={3}
                className="bg-white text-zinc-900 border-zinc-300 placeholder:text-zinc-400"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSubmit} data-testid="create-request-btn">
              Crea Richiesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      {selectedRequest && (
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <span>Richiesta #{selectedRequest.request_number}</span>
                <span className={`px-2 py-0.5 rounded text-xs ${statusColors[selectedRequest.status]}`}>
                  {statusLabels[selectedRequest.status]}
                </span>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              {/* Supplier Info */}
              <div className="p-4 bg-zinc-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-zinc-400">Fornitore</div>
                    <div className="text-white font-semibold">{selectedRequest.supplier_name}</div>
                    <div className="text-zinc-400">{selectedRequest.supplier_phone}</div>
                  </div>
                  <div className="flex gap-2">
                    {selectedRequest.status === "draft" && (
                      <Button onClick={() => sendRequest(selectedRequest)} className="gap-2">
                        <Send className="w-4 h-4" />
                        Invia via WhatsApp
                      </Button>
                    )}
                    {(selectedRequest.status === "sent" || selectedRequest.status === "viewed") && (
                      <Button
                        variant="outline"
                        onClick={() => copyLink(selectedRequest.token)}
                        className="gap-2"
                      >
                        <Copy className="w-4 h-4" />
                        Copia Link
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Vehicle Info */}
              <div>
                <div className="text-sm text-zinc-400 mb-2">Veicolo</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-zinc-800/30 rounded-lg">
                  <div>
                    <div className="text-xs text-zinc-500">Marca/Modello</div>
                    <div className="text-white">
                      {selectedRequest.vehicle?.brand} {selectedRequest.vehicle?.model}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">Targa</div>
                    <div className="text-white">{selectedRequest.vehicle?.plate || "-"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">Anno</div>
                    <div className="text-white">{selectedRequest.vehicle?.year || "-"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">Codice Motore</div>
                    <div className="text-white">{selectedRequest.vehicle?.engine_code || "-"}</div>
                  </div>
                  {selectedRequest.vehicle?.vin && (
                    <div className="md:col-span-4">
                      <div className="text-xs text-zinc-500">VIN</div>
                      <div className="text-white font-mono">{selectedRequest.vehicle.vin}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Parts */}
              <div>
                <div className="text-sm text-zinc-400 mb-2">
                  Ricambi Richiesti ({selectedRequest.parts?.length || 0})
                </div>
                <div className="space-y-2">
                  {selectedRequest.parts?.map((part, index) => {
                    const response = selectedRequest.response_data?.find(
                      (r) => r.part_id === part.part_id
                    );

                    return (
                      <div
                        key={index}
                        className={`p-3 rounded-lg ${
                          response
                            ? (response.availability === "ordinabile" 
                                ? "bg-yellow-500/10 border border-yellow-500/30"
                                : (response.availability === "non_fornibile" || (!response.availability && !response.available))
                                  ? "bg-red-500/10 border border-red-500/30"
                                  : "bg-green-500/10 border border-green-500/30")
                            : "bg-zinc-800/30"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="text-white font-medium">{part.description}</div>
                            {part.code && (
                              <div className="text-xs text-zinc-400 font-mono">{part.code}</div>
                            )}
                            <div className="text-xs text-zinc-500">Qtà: {part.quantity}</div>
                          </div>

                          {response && (() => {
                            const avail = response.availability || (response.available ? "disponibile" : "non_fornibile");
                            if (avail === "disponibile") {
                              return (
                                <div className="text-right">
                                  <div className="flex items-center gap-1 text-green-500">
                                    <CheckCircle2 className="w-4 h-4" />
                                    <span className="text-sm">Disponibile</span>
                                  </div>
                                  <div className="text-white font-semibold">
                                    € {response.price?.toFixed(2)}
                                  </div>
                                  {response.brand_type && (
                                    <div className="text-xs text-zinc-400">{response.brand_type}</div>
                                  )}
                                  {response.delivery_time && (
                                    <div className="text-xs text-zinc-400">Consegna: {response.delivery_time}</div>
                                  )}
                                </div>
                              );
                            } else if (avail === "ordinabile") {
                              return (
                                <div className="text-right">
                                  <div className="flex items-center gap-1 text-yellow-500">
                                    <Clock className="w-4 h-4" />
                                    <span className="text-sm">Ordinabile</span>
                                  </div>
                                  <div className="text-white font-semibold">
                                    € {response.price?.toFixed(2)}
                                  </div>
                                  {response.delivery_time && (
                                    <div className="text-xs text-zinc-400">Consegna: {response.delivery_time}</div>
                                  )}
                                </div>
                              );
                            } else {
                              return (
                                <div className="text-right">
                                  <div className="flex items-center gap-1 text-red-500">
                                    <XCircle className="w-4 h-4" />
                                    <span className="text-sm">Non fornibile</span>
                                  </div>
                                </div>
                              );
                            }
                          })()}

                          {response && response.alternative_description && (
                            <div className="mt-2 p-2 bg-yellow-500/10 rounded text-left">
                              <div className="text-xs text-yellow-500">Alternativa:</div>
                              <div className="text-sm text-white">
                                {response.alternative_description}
                              </div>
                              {response.alternative_price > 0 && (
                                <div className="text-sm text-yellow-400">
                                  € {response.alternative_price.toFixed(2)}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Response Notes */}
              {selectedRequest.response_notes && (
                <div className="p-4 bg-blue-500/10 rounded-lg">
                  <div className="text-sm text-blue-400 mb-1">Note del fornitore:</div>
                  <div className="text-white">{selectedRequest.response_notes}</div>
                </div>
              )}

              {/* Ricambista Quote Response */}
              {selectedRequest.ricambista_response && (
                <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-purple-400">
                      Preventivo dal Ricambista
                    </div>
                    <span className="text-xs text-zinc-400">
                      {selectedRequest.ricambista_response.ricambista_name}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {selectedRequest.ricambista_response.items?.map((item, idx) => {
                      const avail = item.availability || (item.available ? "disponibile" : "non_fornibile");
                      const availLabel = avail === "disponibile" ? "Disponibile" : avail === "ordinabile" ? "Ordinabile" : "Non fornibile";
                      const availColor = avail === "disponibile" ? "text-green-400" : avail === "ordinabile" ? "text-yellow-400" : "text-red-400";
                      return (
                      <div key={idx} className="flex justify-between items-center text-sm py-2 border-b border-purple-500/20 last:border-0">
                        <div>
                          <span className="text-white">{item.description}</span>
                          {item.brand && <span className="text-zinc-400 ml-2">({item.brand})</span>}
                          <span className="text-zinc-500 ml-2">x{item.quantity}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs ${availColor}`}>{availLabel}</span>
                          <span className={avail !== "non_fornibile" ? "text-green-400 font-mono" : "text-red-400"}>
                            {avail !== "non_fornibile" ? `€${item.total?.toFixed(2)}` : "-"}
                          </span>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between items-center mt-4 pt-3 border-t border-purple-500/30">
                    <div>
                      <span className="text-purple-300">Totale:</span>
                      {selectedRequest.ricambista_response.delivery_estimate && (
                        <span className="text-xs text-zinc-400 ml-3">
                          Consegna: {selectedRequest.ricambista_response.delivery_estimate}
                        </span>
                      )}
                    </div>
                    <span className="text-xl font-bold text-purple-400">
                      €{selectedRequest.ricambista_response.total_amount?.toFixed(2)}
                    </span>
                  </div>
                  {selectedRequest.ricambista_response.notes && (
                    <div className="mt-3 p-2 bg-purple-500/10 rounded text-sm text-zinc-300">
                      {selectedRequest.ricambista_response.notes}
                    </div>
                  )}
                  <div className="mt-2 text-xs text-zinc-500">
                    Valido fino: {new Date(selectedRequest.ricambista_response.valid_until).toLocaleDateString('it-IT')}
                  </div>
                </div>
              )}

              {/* Urgency */}
              <div className="flex items-center gap-3">
                <span className="text-zinc-400">Urgenza:</span>
                <span className={`px-2 py-1 rounded ${urgencyColors[selectedRequest.urgency]}`}>
                  {urgencyLabels[selectedRequest.urgency]}
                </span>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowDetailDialog(false);
                  setDeleteConfirm(selectedRequest);
                }}
                className="text-red-500 hover:text-red-400 mr-auto"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Elimina
              </Button>

              {selectedRequest.status === "responded" && (
                <Button onClick={() => openAcceptDialog(selectedRequest)} className="gap-2">
                  <Check className="w-4 h-4" />
                  Accetta Risposta
                </Button>
              )}

              {/* Actions for ricambista quote */}
              {selectedRequest.status === "quotato" && (
                <>
                  <Button
                    variant="destructive"
                    onClick={() => handleRespondToQuote(selectedRequest.request_id, 'reject')}
                    className="gap-2"
                  >
                    <X className="w-4 h-4" />
                    Rifiuta
                  </Button>
                  <Button
                    onClick={() => openRicambistaAcceptDialog(selectedRequest)}
                    className="gap-2 bg-green-600 hover:bg-green-700"
                  >
                    <Check className="w-4 h-4" />
                    Accetta e Ordina
                  </Button>
                </>
              )}

              <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                Chiudi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Accept Response Dialog with Markup */}
      {showAcceptDialog && acceptingRequest && (
        <Dialog open={showAcceptDialog} onOpenChange={() => setShowAcceptDialog(false)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-green-500 flex items-center gap-2">
                <Check className="w-5 h-5" />
                Conferma Accettazione Risposta
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <div className="bg-zinc-900 p-4 rounded border border-zinc-800">
                <p className="text-zinc-400 text-sm mb-2">Richiesta</p>
                <p className="text-white font-semibold">#{acceptingRequest.request_number}</p>
                <p className="text-zinc-500 text-sm mt-1">
                  {acceptingRequest.vehicle?.brand} {acceptingRequest.vehicle?.model} - {acceptingRequest.vehicle?.plate}
                </p>
              </div>

              <div className="bg-zinc-900 p-4 rounded border border-zinc-800">
                <p className="text-zinc-400 text-sm mb-3">Ricambi dalla risposta fornitore:</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {acceptingRequest.response_data?.filter(r => {
                    const avail = r.availability || (r.available ? "disponibile" : "non_fornibile");
                    return avail !== "non_fornibile" || r.alternative_description;
                  }).map((resp, idx) => {
                    const avail = resp.availability || (resp.available ? "disponibile" : "non_fornibile");
                    return (
                    <div key={idx} className="flex justify-between items-center text-sm">
                      <span className="text-white">
                        {avail !== "non_fornibile" ? resp.brand : resp.alternative_brand} - {avail !== "non_fornibile" ? resp.code : resp.alternative_code}
                        {avail === "ordinabile" && <span className="text-yellow-400 text-xs ml-1">(ordinabile)</span>}
                      </span>
                      <span className="text-primary font-['JetBrains_Mono']">
                        € {(avail !== "non_fornibile" ? resp.price : resp.alternative_price)?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <Label className="text-zinc-300">Markup percentuale sul prezzo ricambi</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={markupPercentage}
                    onChange={(e) => setMarkupPercentage(parseInt(e.target.value) || 0)}
                    className="w-24 bg-zinc-900"
                    data-testid="markup-percentage"
                  />
                  <span className="text-zinc-400">%</span>
                  <p className="text-zinc-500 text-sm">
                    I prezzi nel preventivo saranno aumentati di questa percentuale
                  </p>
                </div>
              </div>

              {acceptingRequest.quote_id && (
                <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                  <p className="text-green-400 text-sm">
                    <strong>Preventivo collegato:</strong> I ricambi verranno aggiunti/sostituiti nel preventivo esistente.
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowAcceptDialog(false)}>
                Annulla
              </Button>
              <Button 
                onClick={acceptResponse}
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-accept-response"
              >
                <Check className="w-4 h-4 mr-2" />
                Accetta e Aggiorna Preventivo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Ricambista Quote Accept Dialog with Markup */}
      {showRicambistaAcceptDialog && acceptingRicambistaQuote && (
        <Dialog open={showRicambistaAcceptDialog} onOpenChange={setShowRicambistaAcceptDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-green-500 flex items-center gap-2">
                <Check className="w-5 h-5" />
                Conferma Accettazione Preventivo
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <div className="bg-zinc-900 p-4 rounded border border-zinc-800">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-400">Ricambista:</span>
                  <span className="text-white font-medium">
                    {acceptingRicambistaQuote.ricambista_response?.ricambista_name || "N/D"}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-zinc-400">Totale Fornitore:</span>
                  <span className="text-primary font-['JetBrains_Mono'] text-lg">
                    € {(acceptingRicambistaQuote.ricambista_response?.total_amount || 0).toFixed(2)}
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm text-zinc-400">
                  Percentuale di Ricarico (%)
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={ricambistaMarkup}
                    onChange={(e) => setRicambistaMarkup(parseInt(e.target.value) || 0)}
                    className="w-24 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-white text-center"
                    placeholder="0"
                    data-testid="ricambista-markup-percentage"
                  />
                  <span className="text-zinc-400">%</span>
                  <div className="flex-1 text-right">
                    <span className="text-zinc-400 text-sm">Prezzo finale cliente: </span>
                    <span className="text-green-400 font-['JetBrains_Mono'] text-lg">
                      € {((acceptingRicambistaQuote.ricambista_response?.total_amount || 0) * (1 + ricambistaMarkup / 100)).toFixed(2)}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-zinc-500">
                  Il ricarico verrà applicato ai prezzi degli articoli nel preventivo collegato.
                </p>
              </div>
              
              {acceptingRicambistaQuote.quote_id && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3">
                  <p className="text-blue-400 text-sm">
                    <strong>Preventivo collegato:</strong> I prezzi degli articoli verranno aggiornati automaticamente con il ricarico applicato.
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowRicambistaAcceptDialog(false)}>
                Annulla
              </Button>
              <Button 
                onClick={() => handleRespondToQuote(acceptingRicambistaQuote.request_id, 'accept', ricambistaMarkup)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-ricambista-accept"
              >
                <Check className="w-4 h-4 mr-2" />
                Accetta{ricambistaMarkup > 0 ? ` (+${ricambistaMarkup}% ricarico)` : ""}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-500 flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                Elimina Richiesta
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 space-y-3">
              <p className="text-zinc-300">
                Sei sicuro di voler eliminare questa richiesta ricambi?
              </p>
              <div className="bg-zinc-900 p-3 rounded border border-zinc-800">
                <div className="text-primary font-['JetBrains_Mono'] text-sm">
                  Richiesta #{deleteConfirm.request_number}
                </div>
                <div className="text-white mt-1">
                  {deleteConfirm.vehicle?.brand} {deleteConfirm.vehicle?.model}
                </div>
                <div className="text-zinc-400 text-sm mt-1">
                  {deleteConfirm.parts?.length || 0} ricambi - {deleteConfirm.supplier_name}
                </div>
                {deleteConfirm.quote_id && (
                  <div className="text-yellow-500 text-xs mt-2">
                    Collegata a preventivo
                  </div>
                )}
              </div>
              <p className="text-zinc-500 text-sm">
                Questa azione non può essere annullata.
              </p>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
                Annulla
              </Button>
              <Button 
                variant="destructive"
                onClick={async () => {
                  await deleteRequest(deleteConfirm.request_id, true);
                  setDeleteConfirm(null);
                }}
                data-testid="confirm-delete-request"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Elimina
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
